
import { supabase } from "@/integrations/supabase/client";

export const createNewGame = async (userId?: string, type: 'Personal' | 'Daily' = 'Personal', dailyGameId?: string) => {
  try {
    // First, create a new game record
    const gameData: any = { 
      user_id: userId, 
      status: "in_progress",
      type
    };
    
    // If it's a daily game, add the daily_game_id
    if (type === 'Daily' && dailyGameId) {
      gameData.daily_game_id = dailyGameId;
    }
    
    const { data: newGame, error: gameError } = await supabase
      .from("games")
      .insert([gameData])
      .select()
      .single();
    
    if (gameError) throw gameError;
    
    const gameId = newGame.id;
    
    // For personal games, choose a random word
    // For daily games, use the daily game's initial word
    let randomWord = "hello"; // Default fallback
    let aiExplanation = "Initial word selected to start the game.";
    
    if (type === 'Personal') {
      // Get a random word from the words table using the security definer function
      try {
        const { data, error } = await supabase.rpc('get_random_word_bypass_rls');
        
        if (error) throw error;
        
        if (data) {
          randomWord = data;
        }
      } catch (error) {
        console.error("Error getting random word using RPC:", error);
        
        // Fallback - get a batch of random words
        const { data: wordsData, error: wordsError } = await supabase
          .from("words")
          .select("word")
          .limit(1000) // Get a larger sample
          .order('id');  // Need some order for pagination
        
        if (wordsError) throw wordsError;
        
        // Choose a random word from the returned words
        if (wordsData && wordsData.length > 0) {
          const randomIndex = Math.floor(Math.random() * wordsData.length);
          randomWord = wordsData[randomIndex].word;
        }
      }
    } else if (type === 'Daily' && dailyGameId) {
      // Get the daily game's initial word
      const { data: dailyGameData, error: dailyGameError } = await supabase
        .from("daily_games")
        .select("initial_word")
        .eq("id", dailyGameId)
        .single();
        
      if (dailyGameError) throw dailyGameError;
      
      if (dailyGameData) {
        randomWord = dailyGameData.initial_word;
        aiExplanation = "Today's daily challenge word.";
      }
    }
    
    // Create the first round with the bot's word already chosen
    const { error: roundError } = await supabase
      .from("rounds")
      .insert([{ 
        game_id: gameId, 
        round_number: 1,
        ai_word: randomWord,
        ai_explanation: aiExplanation
      }]);
    
    if (roundError) throw roundError;
    
    return { gameId };
  } catch (error) {
    console.error("Error creating new game:", error);
    throw error;
  }
};

export const getDailyGame = async () => {
  try {
    // Get today's date
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1; // JavaScript months are 0-indexed
    const year = today.getFullYear();
    
    console.log(`Searching for daily game: ${month}/${day}/${year}`);
    
    // Look up today's daily game
    const { data: dailyGameData, error: dailyGameError } = await supabase
      .from("daily_games")
      .select("*")
      .eq("day", day)
      .eq("month", month)
      .eq("year", year)
      .maybeSingle();
      
    if (dailyGameError) {
      console.error("Error fetching daily game:", dailyGameError);
      throw dailyGameError;
    }
    
    if (!dailyGameData) {
      console.log("No daily game found for today");
      
      // For testing, look for any available daily game (most recent)
      const { data: anyDailyGame, error: anyDailyGameError } = await supabase
        .from("daily_games")
        .select("*")
        .order('year', { ascending: false })
        .order('month', { ascending: false })
        .order('day', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (anyDailyGameError) {
        console.error("Error fetching any daily game:", anyDailyGameError);
        return null;
      }
      
      if (anyDailyGame) {
        console.log(`Found most recent daily game: ${anyDailyGame.month}/${anyDailyGame.day}/${anyDailyGame.year}`);
        return anyDailyGame;
      }
      
      return null;
    }
    
    console.log("Found daily game:", dailyGameData);
    return dailyGameData;
  } catch (error) {
    console.error("Error getting daily game:", error);
    throw error;
  }
};

export const checkUserCompletedDailyChallenge = async (userId: string, dailyGameId: string) => {
  if (!userId || !dailyGameId) return false;
  
  try {
    // Check if the user has any games for this daily challenge
    const { data, error } = await supabase
      .from("games")
      .select("*")
      .eq("user_id", userId)
      .eq("daily_game_id", dailyGameId);
      
    if (error) {
      console.error("Error checking daily challenge completion:", error);
      return false;
    }
    
    // If there are any games for this daily challenge, consider it completed
    return data && data.length > 0;
  } catch (error) {
    console.error("Error checking if user completed daily challenge:", error);
    return false;
  }
};

// Helper to get time until next day (midnight)
export const getTimeUntilNextDailyChallenge = (): {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
} => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const diffMs = tomorrow.getTime() - now.getTime();
  const totalSeconds = Math.floor(diffMs / 1000);
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return { hours, minutes, seconds, totalSeconds };
};
